import { i18n } from '../../translations'
import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
import type { Conversation } from '@/types/Conversation'
import type { Credits } from '@/types/Credits'
import type { ExpandElement } from '@/types/ExpandElement'
import type { ExternalLink } from '@/types/ExternalLink'
import type { Folder } from '@/types/Folder'
import type { FrameBlock } from '@/types/FrameBlock'
import type { GlossCollectionType } from '@/types/GlossCollectionType'
import type { IFlytTask } from '@/types/Flyt'
import type { LongformText } from '@/types/LongformText'
import type { Presentation } from '@/types/presentation/Presentation'
import type { Size, ThinkText } from '@/types/ThinkText'
import type { Solution } from '@/types/Solution'
import type {
  AnnualPlan, Subject, SubjectContent, SubjectResource 
} from '@/types/Subject'
import type { SubjectGuide } from '@/types/SubjectGuide'
import type { StepTask, TaskFrontpage } from '@/types/Task'
import { TaskImageAlign } from '@/types/Task'
import type { TeacherGuide } from '@/types/TeacherGuide'
import type { TextImage } from '@/types/TextImage'
import { TextImageImageAlign } from '@/types/TextImage'
import type { Topic } from '@/types/Topic'
import type { TopSection } from '@/types/TopSection'
import type { VgsContentBlock } from '@/types/VgsContentBlock'
import { formatProgramLabel, removeXmlTags } from '@/utils'
import IbexaColorPair from '@/enums/IbexaColorPair'
import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
import type {
  IbexaAccess,
  IbexaBaseItemFields,
  IbexaField,
  IbexaFile,
  IbexaImageField,
  IbexaRelationField,
  IbexaRichTextField,
  IbexaTagsField,
  IbexaUrl,
  Item,
  MetadataField,
} from '@/types/Ibexa'
import type { Slide } from '@/types/presentation/Slide'
import type { VgsVideo } from '@/types/VgsVideo'
import SlideFontSize from '@/enums/SlideFontSize'
import SlideElementPosition from '@/enums/SlideElementPosition'
import type SlideTextPosition from '@/enums/SlideTextPosition'
import type { PronunciationCollection } from '@/types/PronunciationCollection'
import type { Pronunciation } from '@/types/Pronunciation'

const { t } = i18n.global

const getFields = (item: Item) => {
  return item.value.Location.ContentInfo.Content.CurrentVersion.Version.Fields.field
}

const getDepth = (item: Item) => {
  return item.value.Location.depth
}

export const findField = (fields: IbexaField[], fieldIdentifier: string) => fields.find(field => field.fieldDefinitionIdentifier === fieldIdentifier)?.fieldValue

export const getItemField = (item: Item, fieldName: string) => {
  return getFields(item).find((field: IbexaField) => field.fieldDefinitionIdentifier === fieldName)?.fieldValue
}

const getItemFieldType = (item: Item, fieldType: string) => {
  return getFields(item).find((field: IbexaField) => field.fieldTypeIdentifier === fieldType)?.fieldValue
}

const getParentLocationId = (item: Item) => {
  const hrefArray = item.value.Location.ParentLocation._href.split('/')
  return hrefArray[hrefArray.length - 1]
}

const getRichTextItemByFieldName = (item: Item, fieldName: string) => {
  const richTextFieldValue = getFields(item).find((field: IbexaField) => field.fieldDefinitionIdentifier === fieldName)?.fieldValue as IbexaRichTextField
  if (!richTextFieldValue) return ''

  return removeXmlTags(richTextFieldValue.xhtml5output)?.trim()
}

export const getItemContentId = (item: Item): number => {
  return item.value.Location.ContentInfo.Content._id
}

export const getContentTypeIdentifier = (item: Item): IbexaContentTypeIdentifier => {
  return item.value.Location.ContentInfo.Content.ContentType._identifier as IbexaContentTypeIdentifier
}

const getBaseItemFields = (item: Item): IbexaBaseItemFields => {
  return {
    title: getItemField(item, 'title_with_html') as string ?? getItemField(item, 'title') as string,
    locationId: getItemLocationId(item),
    contentId: getItemContentId(item),
    contentTypeIdentifier: getContentTypeIdentifier(item),
    pathString: getItemPathString(item),
    isTeacherOnly: getItemAccess(item) === 'teacher',
    depth: getDepth(item),
    parentLocationId: getParentLocationId(item),
  }
}

const getItemAccess = (item: Item): IbexaAccess => item.value.Location.ContentInfo.Content.ObjectStates._access

export const getItemLocationId = (item: Item): string => {
  const locationPath = item.value.Location.ContentInfo.Content.MainLocation?._href?.split('/')
  return locationPath?.[locationPath.length - 1] ?? ''
}

const getItemPathString = (item: Item): string => item.value.Location.pathString

export const getItemContentType = (item: Item): string => item.value.Location.ContentInfo.Content.ContentType._identifier

const getItemRichText = (item: Item): string => {
  const richTextField = getItemFieldType(item, 'ezrichtext') as IbexaRichTextField
  return removeXmlTags(richTextField?.xhtml5output)
}

const getUrl = (item: Item): IbexaUrl => {
  return getItemFieldType(item, 'ezurl') as IbexaUrl
}

const getFirstTagFieldKeywordFromItem = (item: Item, field: string) => {
  const fieldValue = getItemField(item, field) as IbexaTagsField
  return getFirstTagFieldKeyword(fieldValue)
}

export const getFirstTagFieldKeyword = (tagField: IbexaTagsField) => tagField?.[0]?.keywords['nor-NO'] as string ?? undefined

const getItemColorPair = (item: Item): IbexaColorPair => {
  return (getFirstTagFieldKeywordFromItem(item, 'color_pair') ?? IbexaColorPair.GreenCoral) as IbexaColorPair
}

const getSubjectProgramLabel = (item: Item) => {
  const level = getFirstTagFieldKeywordFromItem(item, 'levels')
  const program = getFirstTagFieldKeywordFromItem(item, 'programs')
  return formatProgramLabel(program, level)
}

export const mapSubject = (item: Item): Subject => {
  const relationList = getItemField(item, 'authors') as Record<string, string[]>
  const title = getItemField(item, 'short_title') as string ?? getItemField(item, 'title') as string
  const annualPlanRelation = getItemField(item, 'annual_plan') as IbexaRelationField

  return {
    ...getBaseItemFields(item),
    title,
    label: getSubjectProgramLabel(item),
    colorPair: getItemColorPair(item),
    image: getItemField(item, 'image') as IbexaImageField,
    authorIds: relationList?.destinationContentIds?.map((id: string) => parseInt(id)),
    annualPlanId: annualPlanRelation?.destinationContentId,
  }
}

export const mapAnnualPlan = (item: Item): AnnualPlan => {
  const name = (getItemField(item, 'short_name') || getItemField(item, 'name')) as string
  const ibexaFile = getItemField(item, 'file') as unknown as IbexaFile
  const baseFields = getBaseItemFields(item)
  
  return {
    ...baseFields,
    file: ibexaFile && {
      name,
      fileName: ibexaFile.fileName, 
      mimeType: ibexaFile.mimeType,
      bytes: ibexaFile.fileSize,
      contentId: baseFields.contentId
    }
  }}


export const mapSubjectContent = (item: Item): SubjectContent => {
  return {
    ...getBaseItemFields(item),
    category: getFirstTagFieldKeywordFromItem(item, 'category') as string
  }
}

export const mapSubjectResource = (item: Item): SubjectResource => {
  const introductionStepField = getItemField(item, 'introduction_step') as IbexaRelationField
  const introductionStepLabel = getItemField(item, 'introduction_step_label') as Record<number, string>

  return {
    ...getBaseItemFields(item),
    image: getItemField(item, 'thumbnail') as IbexaImageField,
    category: getFirstTagFieldKeywordFromItem(item, 'category') as string,
    description: getRichTextItemByFieldName(item, 'description'),
    introduction: getRichTextItemByFieldName(item, 'introduction'),
    isStepSuperTitle: getItemField(item, 'is_step_super_title') as boolean,
    isNumberBullets: getItemField(item, 'is_number_bullets') as boolean,
    isAnchorNavigationTask: getItemField(item, 'is_anchor_navigation_task') as boolean,
    introductionStepId: introductionStepField?.destinationContentId,
    introductionStepLabel: introductionStepLabel?.[0]?.includes('Forberedelse') ? t('preparation') : t('introduction'),
  }
}

export const mapTopicGuide = (item: Item): AnchorNavigationPage => {
  return {
    ...getBaseItemFields(item),
    description: getRichTextItemByFieldName(item, 'description'),
    image: getItemField(item, 'image') as IbexaImageField,
    sectionTitles: [
      t('topicGuide.justification'),
      t('topicGuide.preparations'),
      t('topicGuide.differentiation'),
      t('topicGuide.evaluation'),
      t('topicGuide.execution'),
    ],
    sections: [
      getRichTextItemByFieldName(item, 'justification'),
      getRichTextItemByFieldName(item, 'preparations'),
      getRichTextItemByFieldName(item, 'differentiation'),
      getRichTextItemByFieldName(item, 'evaluation'),
      getRichTextItemByFieldName(item, 'execution'),
    ]
  }
}

export const mapResourceGuide = (item: Item): AnchorNavigationPage => {
  return {
    ...getBaseItemFields(item),
    description: getRichTextItemByFieldName(item, 'description'),
    image: getItemField(item, 'image') as IbexaImageField,
    sectionTitles: [
      t('resourceGuide.what'),
      t('resourceGuide.how'),
      t('resourceGuide.why'),
    ],
    sections: [
      getRichTextItemByFieldName(item, 'what'),
      getRichTextItemByFieldName(item, 'how'),
      getRichTextItemByFieldName(item, 'why'),
    ]
  }
}

export const mapSubjectGuide = (item: Item): SubjectGuide => {
  return {
    ...getBaseItemFields(item),
    introduction: getRichTextItemByFieldName(item, 'introduction'),
    description: getRichTextItemByFieldName(item, 'description'),
    image: getItemField(item, 'image') as IbexaImageField,
  }
}

export const mapTeacherGuide = (item: Item): TeacherGuide => {
  return {
    ...getBaseItemFields(item),
    description: getRichTextItemByFieldName(item, 'description'),
  }
}

export const mapCmsTopic = (item: Item): Topic => {
  return {
    ...getBaseItemFields(item),
    subTitle: getItemField(item, 'subtitle') as string,
    introduction: getRichTextItemByFieldName(item, 'intro'),
    isComingSoon: getItemField(item, 'is_coming_soon') as boolean,
  }
}

export const mapCmsFolder = (item: Item): Folder => {
  return {
    ...getBaseItemFields(item),
    image: getItemField(item, 'image') as IbexaImageField,
  }
}

export const mapCmsCredits = (item: Item): Credits => {
  const authors = getItemField(item, 'authors') as unknown as Record<string, number[]>
  const editors = getItemField(item, 'editors') as unknown as Record<string, number[]>

  const data = {
    authorIds: authors?.destinationContentIds,
    editorIds: editors?.destinationContentIds,
    collaborators: getRichTextItemByFieldName(item, 'collaborators'),
    bibliography: getRichTextItemByFieldName(item, 'bibliography'),
  }

  return data
}

export const mapCmsFrameBlock = (item: Item): FrameBlock => {
  return {
    ...getBaseItemFields(item),
    colorPair: getItemColorPair(item),
    description: getRichTextItemByFieldName(item, 'description'),
    label: getItemField(item, 'label') as string,
  }
}

export const mapCmsExpandElement = (item: Item): ExpandElement => {
  return {
    ...getBaseItemFields(item),
    content: getItemRichText(item)
  }
}

export const mapCmsTextImageElement = (item: Item): TextImage => {
  const imageAlignFieldValue = getItemField(item, 'view_option') as Record<number, TextImageImageAlign>

  return {
    ...getBaseItemFields(item),
    richText: getItemRichText(item),
    colorPair: getItemColorPair(item),
    imageAlign: imageAlignFieldValue?.[0],
    image: getItemField(item, 'image') as IbexaImageField,
    svg: getItemField(item, 'svg_relation') as IbexaRelationField,
    imageAlt: getItemField(item, 'image_alt') as string,
    imageCaption: getItemField(item, 'image_caption') as string,
  }
}

export const getSizeFieldOrDefault = (item: Item): Size => {
  const size = getItemField(item, 'size')
  return (Array.isArray(size) && size.length > 0) ? size[0] as Size : 'large'
}

export const mapCmsThinkTextElement = (item: Item): ThinkText => {
  return {
    ...getBaseItemFields(item),
    richText: getItemRichText(item),
    colorPair: getItemColorPair(item),
    size: getSizeFieldOrDefault(item)
  }
}

export const mapCmsTopSectionElement = (item: Item): TopSection => {
  const relationList = getItemField(item, 'authors') as Record<string, string[]>

  return {
    ...getBaseItemFields(item),
    introduction: getRichTextItemByFieldName(item, 'introduction_rich_text') as string || getItemField(item, 'introduction') as string,
    label: getItemField(item, 'label') as string,
    authorIds: relationList?.destinationContentIds?.map((id: string) => parseInt(id)),
    colorPair: getItemColorPair(item),
    image: getItemField(item, 'image') as IbexaImageField
  }
}

export const mapCmsTask = (item: Item): StepTask => {
  const relatedContentIds = getItemField(item, 'related_content') as Record<string, string[]>
  const imageAlignFieldValue = getItemField(item, 'view_option') as Record<number, TaskImageAlign>

  return {
    ...getBaseItemFields(item),
    description: getRichTextItemByFieldName(item, 'description') as string,
    isNoAidTask: getItemField(item, 'is_no_aid_task') as boolean,
    rightColumnRichText: getRichTextItemByFieldName(item, 'right_column_rich_text'),
    subtasks: [
      getRichTextItemByFieldName(item, 'subtask1'),
      getRichTextItemByFieldName(item, 'subtask2'),
      getRichTextItemByFieldName(item, 'subtask3'),
      getRichTextItemByFieldName(item, 'subtask4')
    ],
    descriptionAfterSubtasks: getRichTextItemByFieldName(item, 'description_after_subtasks'),
    hint: getRichTextItemByFieldName(item, 'hint') as string,
    imageAlign: imageAlignFieldValue?.[0],
    image: getItemField(item, 'image') as IbexaImageField,
    relatedResourceContentIds: relatedContentIds?.destinationContentIds.map((id: string) => parseInt(id)),
    svg: getItemField(item, 'svg_relation') as IbexaRelationField,
    imageAlt: getItemField(item, 'image_alt') as string,
  }
}

export const mapTaskFrontpage = (item: Item): TaskFrontpage => ({
  ...getBaseItemFields(item),
  description: getRichTextItemByFieldName(item, 'description'),
  image: getItemField(item, 'image') as IbexaImageField,
  colorPair: getItemColorPair(item),
  svg: getItemField(item, 'svg_relation') as IbexaRelationField,
  imageAlt: getItemField(item, 'image_alt') as string,
  imageCaption: getItemField(item, 'image_caption') as string,
})

export const mapCmsLongformText = (item: Item): LongformText => ({
  ...getBaseItemFields(item),
  title: getItemField(item, 'name') as string,
  richText: getItemRichText(item)
})

export const mapCmsExternalLinks = (item: Item): ExternalLink => (
  {
    ...getBaseItemFields(item),
    label: getItemField(item, 'label') as string,
    url: getUrl(item) as IbexaUrl,
  })


export const mapCmsSolution = (item: Item): Solution => {
  return {
    ...getBaseItemFields(item),
    description: getRichTextItemByFieldName(item, 'description'),
    solution: getRichTextItemByFieldName(item, 'solution'),
  }
}

export const mapCmsFlytTask = (item: Item): IFlytTask => {
  return {
    ...getBaseItemFields(item),
    id: getItemField(item, 'flyt_task_id') as string,
  }
}

export const mapGlossCollection = (item: Item): GlossCollectionType => {
  const { destinationContentIds: glossIds } = getItemField(item, 'entries') as Record<string, number[]>
  return {
    ...getBaseItemFields(item),
    glossIds,
  }
}

export const mapCmsConversation = (item: Item): Conversation => {
  const { destinationContentIds: sentenceIds } = getItemField(item, 'sentences') as Record<string, number[]>

  return {
    ...getBaseItemFields(item),
    sentenceIds
  }
}

export const mapPresentation = (item: Item): Presentation => {
  const slides = getItemField(item, 'slides') as Record<string, number[]>
  const resourceGuideField = getItemField(item, 'resource_guide') as IbexaRelationField

  return {
    ...getBaseItemFields(item),
    slides: slides.destinationContentIds,
    colorPair: getItemColorPair(item),
    resourceGuide: parseInt(resourceGuideField?.destinationContentId),
  }
}

export const mapSlide = (item: Item): Slide => {
  return {
    ...getBaseItemFields(item),
    video: getItemField(item, 'video') as IbexaRelationField,
    image: getItemField(item, 'image') as IbexaImageField,
    heading: getItemField(item, 'heading') as string,
    headingFontSize: getItemField(item, 'heading_font_size') as SlideFontSize ?? SlideFontSize.Medium,
    text: getRichTextItemByFieldName(item, 'text'),
    textFontSize: getItemField(item, 'text_font_size') as SlideFontSize ?? SlideFontSize.Medium,
    mainElementPosition: getItemField(item, 'main_element_position') as SlideElementPosition,
    textPosition: getItemField(item, 'text_position') as SlideTextPosition ?? 'top-left',
  }
}

export const mapPronunciationCollection = (item: Item): PronunciationCollection => {
  const { destinationContentIds: pronunciationIds } = getItemField(item, 'pronunciation_boxes') as Record<string, number[]>

  return {
    ...getBaseItemFields(item),
    pronunciationIds,
    heading: getItemField(item, 'heading') as string,
    description: getRichTextItemByFieldName(item, 'description'),
    pagination: getItemField(item, 'pagination') as boolean,
    blueBackground: getItemField(item, 'blue_background') as boolean,
  }
}

export const mapPronunciation = (item: Item): Pronunciation => {
  return {
    ...getBaseItemFields(item),
    heading: getItemField(item, 'title') as string,
    description: getRichTextItemByFieldName(item, 'description'),
    wordRelation: getItemField(item, 'word') as IbexaRelationField,
    audioFileRelation: getItemField(item, 'audio_file') as IbexaRelationField,
  }

}

export const mapVideo = (item: Item): VgsVideo => {
  const metadata = getItemField(item, 'metadata') as MetadataField

  return {
    ...getBaseItemFields(item),
    caption: getItemField(item, 'caption') as string ?? '',
    size: getItemField(item, 'size') as number ?? -1,
    src: metadata.elementURI,
    posterSrc: metadata.image ?? '',
  }
}

export const mapCmsContent = (item: Item): VgsContentBlock | IbexaBaseItemFields => {
  const identifierName = getContentTypeIdentifier(item)
  switch (identifierName) {
    case IbexaContentTypeIdentifier.vgsThinkTextElement:
      return mapCmsThinkTextElement(item)
    case IbexaContentTypeIdentifier.vgsFrameBlock:
      return mapCmsFrameBlock(item)
    case IbexaContentTypeIdentifier.vgsExpandElement:
      return mapCmsExpandElement(item)
    case IbexaContentTypeIdentifier.vgsTextImageElement:
      return mapCmsTextImageElement(item)
    case IbexaContentTypeIdentifier.vgsTopSectionElement:
      return mapCmsTopSectionElement(item)
    case IbexaContentTypeIdentifier.vgsStepTask:
      return mapCmsTask(item)
    case IbexaContentTypeIdentifier.vgsTaskFrontpage:
      return mapTaskFrontpage(item)
    case IbexaContentTypeIdentifier.vgsLongformText:
      return mapCmsLongformText(item)
    case IbexaContentTypeIdentifier.vgsSolution:
      return mapCmsSolution(item)
    case IbexaContentTypeIdentifier.vgsExternalLink:
      return mapCmsExternalLinks(item)
    case IbexaContentTypeIdentifier.vgsFlytTask:
      return mapCmsFlytTask(item)
    case IbexaContentTypeIdentifier.vgsTeacherGuide:
      return mapTeacherGuide(item)
    case IbexaContentTypeIdentifier.vgsTopicGuide:
      return mapTopicGuide(item)
    case IbexaContentTypeIdentifier.vgsProduct:
      return mapSubject(item)
    case IbexaContentTypeIdentifier.vgsTopic:
      return mapCmsTopic(item)
    case IbexaContentTypeIdentifier.vgsFolder:
      return mapCmsFolder(item)
    case IbexaContentTypeIdentifier.vgsGlossCollection:
      return mapGlossCollection(item)
    case IbexaContentTypeIdentifier.vgsConversation:
      return mapCmsConversation(item)
    case IbexaContentTypeIdentifier.vgsPresentation:
      return mapPresentation(item)
    case IbexaContentTypeIdentifier.vgsSubjectResource:
      return mapSubjectResource(item)
    case IbexaContentTypeIdentifier.vgsSubjectContent:
      return mapSubjectContent(item)
    case IbexaContentTypeIdentifier.vgsVideo:
      return mapVideo(item)
    case IbexaContentTypeIdentifier.vgsPronunciationBoxCollection:
      return mapPronunciationCollection(item)
    default:
      console.warn(`Found unmapped identifier named: ${identifierName}`)
      return getBaseItemFields(item)
  }
}
